import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    Avatar,
    Card,
    CardContent,
    Alert,
    IconButton,
    InputAdornment,
    CircularProgress,
    Snackbar,
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { profileService, UserProfile } from '../services';

// Validation schemas
const profileSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup.string().optional(),
    role: yup.string().optional(),
});

const passwordSchema = yup.object({
    currentPassword: yup.string().required('Current password is required'),
    newPassword: yup
        .string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

type ProfileFormData = yup.InferType<typeof profileSchema>;
type PasswordFormData = yup.InferType<typeof passwordSchema>;

const ProfilePage: React.FC = () => {
    const { user } = useAuth();
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileUpdateSuccess, setProfileUpdateSuccess] = useState(false);
    const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // API integration states
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState<UserProfile | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorOpen, setErrorOpen] = useState(false);

    // Derive name and email from profileData or user
    const derivedFirstName = profileData?.firstName || user?.username?.split(' ')[0] || user?.username || '';
    const derivedLastName = profileData?.lastName || user?.username?.split(' ')[1] || '';
    const derivedEmail = profileData?.email || (user?.username ? `${user.username.toLowerCase().replace(/\s+/g, '.')}@carex.com` : '');
    const derivedPhone = profileData?.phone || '';

    const {
        control: profileControl,
        handleSubmit: handleProfileSubmit,
        formState: { errors: profileErrors },
        reset: resetProfile,
        setValue: setProfileValue,
    } = useForm<ProfileFormData>({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            firstName: derivedFirstName,
            lastName: derivedLastName,
            email: derivedEmail,
            phone: derivedPhone,
            role: user?.role || '',
        },
    });

    const {
        control: passwordControl,
        handleSubmit: handlePasswordSubmit,
        formState: { errors: passwordErrors },
        reset: resetPassword,
    } = useForm<PasswordFormData>({
        resolver: yupResolver(passwordSchema),
    });

    // Fetch profile data on component mount
    useEffect(() => {
        fetchProfile();
    }, []);

    // Update form when profileData changes
    useEffect(() => {
        if (profileData) {
            setProfileValue('firstName', profileData.firstName);
            setProfileValue('lastName', profileData.lastName);
            setProfileValue('email', profileData.email);
            setProfileValue('phone', profileData.phone || '');
            setProfileValue('role', profileData.role);
        }
    }, [profileData, setProfileValue]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const data = await profileService.getProfile();
            setProfileData(data);
        } catch (err: any) {
            console.error('Failed to fetch profile:', err);
            // If API fails, use mock data from user context
            setError(err.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const onProfileSubmit = async (data: ProfileFormData) => {
        try {
            setLoading(true);
            setError(null);

            const updatedProfile = await profileService.updateProfile({
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: data.phone,
            });

            setProfileData(updatedProfile);
            setProfileUpdateSuccess(true);
            setIsEditingProfile(false);
            setTimeout(() => setProfileUpdateSuccess(false), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to update profile');
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const onPasswordSubmit = async (data: PasswordFormData) => {
        try {
            setLoading(true);
            setError(null);

            await profileService.changePassword({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
            });

            setPasswordUpdateSuccess(true);
            resetPassword();
            setTimeout(() => setPasswordUpdateSuccess(false), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to change password');
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        resetProfile();
        setIsEditingProfile(false);
    };

    const getInitials = () => {
        return `${derivedFirstName.charAt(0) || ''}${derivedLastName.charAt(0) || ''}`.toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U';
    };

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                My Profile
            </Typography>

            {/* Loading Indicator */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error Snackbar */}
            <Snackbar
                open={errorOpen}
                autoHideDuration={6000}
                onClose={() => setErrorOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setErrorOpen(false)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Grid container spacing={3}>
                {/* Profile Information Card */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    Personal Information
                                </Typography>
                                {!isEditingProfile && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => setIsEditingProfile(true)}
                                    >
                                        Edit Profile
                                    </Button>
                                )}
                            </Box>

                            {profileUpdateSuccess && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    Profile updated successfully!
                                </Alert>
                            )}

                            <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="firstName"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="First Name"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.firstName}
                                                    helperText={profileErrors.firstName?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="lastName"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Last Name"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.lastName}
                                                    helperText={profileErrors.lastName?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="email"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Email"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.email}
                                                    helperText={profileErrors.email?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="phone"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Phone Number"
                                                    disabled={!isEditingProfile}
                                                    error={!!profileErrors.phone}
                                                    helperText={profileErrors.phone?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="role"
                                            control={profileControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Role"
                                                    disabled
                                                    error={!!profileErrors.role}
                                                    helperText={profileErrors.role?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>

                                {isEditingProfile && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                )}
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Avatar and Quick Info Card */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <Avatar
                                sx={{
                                    width: 120,
                                    height: 120,
                                    margin: '0 auto',
                                    fontSize: '3rem',
                                    bgcolor: 'primary.main',
                                    mb: 2,
                                }}
                            >
                                {getInitials()}
                            </Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {derivedFirstName} {derivedLastName || user?.username}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                {derivedEmail}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 2,
                                    display: 'inline-block',
                                    mt: 2,
                                }}
                            >
                                {user?.role?.replace(/_/g, ' ')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Change Password Card */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                                Change Password
                            </Typography>

                            {passwordUpdateSuccess && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    Password changed successfully!
                                </Alert>
                            )}

                            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="currentPassword"
                                            control={passwordControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Current Password"
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    error={!!passwordErrors.currentPassword}
                                                    helperText={passwordErrors.currentPassword?.message}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="newPassword"
                                            control={passwordControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="New Password"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    error={!!passwordErrors.newPassword}
                                                    helperText={passwordErrors.newPassword?.message}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="confirmPassword"
                                            control={passwordControl}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Confirm New Password"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    error={!!passwordErrors.confirmPassword}
                                                    helperText={passwordErrors.confirmPassword?.message}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                                disabled={loading}
                                            >
                                                {loading ? 'Changing...' : 'Change Password'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfilePage;
